import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { MsalInterceptor } from '@azure/msal-angular';
import { Observable } from 'rxjs';

import { CandidateSessionService } from './candidate-session.service';

/**
 * KCS-565 Part 2 — wraps the MSAL HTTP interceptor so candidate (temp user) sessions are left alone.
 *
 * <p>On Microsoft-login instances {@link MsalInterceptor} attaches an Azure token to every {@code /api/*}
 * call and redirects token-less users to Microsoft. A candidate has no Azure token — letting MSAL touch
 * their requests would break the call and bounce them to the Microsoft login. This delegate forwards the
 * request untouched when a candidate session is active (the candidate's own Bearer is added elsewhere) and
 * otherwise behaves exactly like the stock MSAL interceptor for staff.</p>
 *
 * <p>It must be a class interceptor delegating to a plain-injectable {@code MsalInterceptor}: a functional
 * interceptor ordered earlier cannot prevent a downstream DI interceptor from running.</p>
 */
@Injectable()
export class CandidateAwareMsalInterceptor implements HttpInterceptor {
  constructor(
    private readonly msalInterceptor: MsalInterceptor,
    private readonly candidateSession: CandidateSessionService
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Skip MSAL when a candidate session is active, OR when the call is a public candidate auth endpoint.
    // The login endpoints fire BEFORE the candidate token exists (isActive() is still false), so without
    // this URL guard MSAL would match its /api/* rule, find no Azure account, and redirect the candidate's
    // own login request to the Microsoft "pick an account" page.
    if (this.candidateSession.isActive() || this.isCandidateAuthEndpoint(req.url)) {
      return next.handle(req);
    }
    return this.msalInterceptor.intercept(req, next);
  }

  /**
   * Public KCS-565 candidate login endpoints (username+password and QR resolve/confirm). Matched without the
   * `/api` prefix because SERVER_API_URL is '' — the request URL arrives as `api/authentication/...` (no
   * leading slash), so a `/api/...` check would miss it and let MSAL hijack the candidate's login.
   */
  private isCandidateAuthEndpoint(url: string): boolean {
    return url.includes('/authentication/credentials') || url.includes('/authentication/qr/')
      || url.includes('/authentication/lite-'); // LB-1893: Leanbyte Lite
  }
}
